import classNames from "classnames";
import { ReactNode } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch } from "react-redux";
import ReactTooltip from "react-tooltip";

import { AnnotationType, ViewerMode } from "custom-models/ViewerTypes";

import {
  addAnnotation,
  changeViewerMode,
  resetNewPenDrawing,
} from "shared/actions/slideDetailActions";
import { AIState } from "shared/reducers/aiReducer";
import { AuthState } from "shared/reducers/authReducer";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import { notFound } from "shared/utils/annotations";

import { useAppSelector } from "store/hooks";

type AnnotationToolButtonProps = {
  wantedViewerMode: ViewerMode;
  slideDetailObject: SlideDetailState;
  aiObject?: AIState;
  authObject: AuthState;
  title: string;
  children: ReactNode;
  mppRequired?: boolean;
  maskRequired?: boolean;
  hotkey?: string;
};

const AnnotationToolButton = ({
  slideDetailObject,
  wantedViewerMode,
  title,
  children,
  mppRequired,
  maskRequired,
  hotkey,
}: AnnotationToolButtonProps) => {
  const { viewerMode, mpp } = slideDetailObject;
  const dispatch = useDispatch();

  const maskLesionLineType = useAppSelector((app) => app.ai.maskLesionLineType);
  const kwonKeys = useAppSelector((state) =>
    selectFeatureFlag(state, "kwonKeys")
  );

  const handleAnyNewPenDrawing = () => {
    const { newPenDrawing, pendingAnnotationsBuffer } = slideDetailObject;
    if (
      newPenDrawing &&
      slideDetailObject.id &&
      notFound(pendingAnnotationsBuffer, newPenDrawing)
    ) {
      if (
        newPenDrawing.type === AnnotationType.PEN_DRAWING &&
        newPenDrawing.pathInfo.indexOf("L") === -1
      ) {
        dispatch(resetNewPenDrawing());
      } else {
        dispatch(addAnnotation(newPenDrawing, slideDetailObject.id));
      }
    }
  };

  const mppUnavailable = mppRequired && !mpp;

  const handleStartAnnotation = mppUnavailable
    ? undefined
    : () => {
        handleAnyNewPenDrawing();
        dispatch(changeViewerMode(wantedViewerMode));
      };
  const cancelStartAnnotation = () => {
    dispatch(resetNewPenDrawing());
    dispatch(changeViewerMode(ViewerMode.PAN_ZOOM));
  };

  useHotkeys(
    hotkey ?? "",
    () => {
      if (viewerMode !== wantedViewerMode) {
        handleStartAnnotation && handleStartAnnotation();
      } else if (viewerMode === wantedViewerMode) {
        cancelStartAnnotation();
      }
    },
    { enabled: !!hotkey && kwonKeys, scopes: ["viewerHotkeys"] },
    [viewerMode, wantedViewerMode]
  );

  useHotkeys(
    "Escape",
    () => {
      if (viewerMode === wantedViewerMode) {
        cancelStartAnnotation();
      }
    },
    {
      enabled: !!hotkey && viewerMode === wantedViewerMode && kwonKeys,
      scopes: ["viewerHotkeys"],
    },
    [viewerMode, wantedViewerMode]
  );

  return (
    <>
      <button
        title={title}
        disabled={maskRequired && !maskLesionLineType}
        className={classNames(
          viewerMode === wantedViewerMode ? "active" : undefined,
          (mppUnavailable || (maskRequired && !maskLesionLineType)) &&
            "unavailable"
        )}
        onClick={handleStartAnnotation}
        data-tip=""
        data-for={`button-${title}`}
      >
        {children}
      </button>
      <ReactTooltip
        place="bottom"
        id={`button-${title}`}
        type={mppUnavailable ? "error" : "info"}
      >
        {title}
        {mppUnavailable && " (MPP Not Available)"}
      </ReactTooltip>
    </>
  );
};

export default AnnotationToolButton;
